/**
 * Pantalla de ingreso: «Entrar con Google» y, solo en desarrollo, entrada por correo.
 *
 * Si Google devuelve un error, la API vuelve a la web con `?error=<codigo>`; aquí se traduce
 * a un mensaje que la persona entienda.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { mensajeDeError } from '../../lib/api';
import { entrarEnDesarrollo, obtenerConfiguracion } from './api';
import './PantallaIngreso.css';

/** Mensajes para los códigos de error que la API manda al volver de Google. */
export function mensajeDeIngreso(codigo: string | null, dominio: string): string | null {
  if (!codigo) return null;
  const mensajes: Record<string, string> = {
    inicio_cancelado: 'El inicio de sesión se canceló o venció. Intenta de nuevo.',
    estado_invalido: 'No se pudo verificar la seguridad del inicio de sesión. Intenta de nuevo.',
    prohibido: `Solo pueden entrar cuentas activas de @${dominio}. Si es la tuya, pide a un administrador que la active.`,
    google_fallo: 'Google no pudo completar el inicio de sesión. Intenta de nuevo en un momento.',
  };
  return mensajes[codigo] ?? 'No se pudo iniciar sesión. Intenta de nuevo.';
}

export function PantallaIngreso() {
  const cliente = useQueryClient();
  const [correo, setCorreo] = useState('');
  const configuracion = useQuery({ queryKey: ['configuracion'], queryFn: obtenerConfiguracion });
  const entrar = useMutation({
    mutationFn: entrarEnDesarrollo,
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['yo'] }),
  });

  const dominio = configuracion.data?.dominioPermitido ?? 'cdtec.cl';
  const codigoError = new URLSearchParams(window.location.search).get('error');
  const mensajeInicial = mensajeDeIngreso(codigoError, dominio);

  const alEnviar = (evento: FormEvent) => {
    evento.preventDefault();
    entrar.mutate(correo.trim());
  };

  return (
    <main className="ingreso">
      <h1>Tablero CDTEC</h1>
      <p>Backlog de CDTEC IF: lo pendiente, quién lo hace y en qué etapa va.</p>

      {mensajeInicial && (
        <p className="ingreso__error" role="alert">
          {mensajeInicial}
        </p>
      )}

      <a className="boton boton--primario ingreso__google" href="/api/auth/google">
        Entrar con Google
      </a>
      <p className="ingreso__nota">Solo cuentas de @{dominio}.</p>

      {configuracion.data?.loginDesarrolloHabilitado && (
        <form className="ingreso__dev" onSubmit={alEnviar}>
          <h2>Entrada de desarrollo</h2>
          <div className="campo">
            <label htmlFor="correo-dev">Correo</label>
            <input
              id="correo-dev"
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              required
              placeholder="tu.nombre@cdtec.cl"
            />
          </div>
          <button className="boton" type="submit" disabled={entrar.isPending}>
            Entrar sin Google
          </button>
          {entrar.isError && (
            <p className="ingreso__error" role="alert">
              {mensajeDeError(entrar.error)}
            </p>
          )}
        </form>
      )}
    </main>
  );
}
